import type { IRecordTypeDefinition, IRecordTypeOverride } from '@platform/core/types';
import { editorWritten } from './formRules';
import { formSourceOf } from './formSourceOf';
import type { FormSourceKind } from './FormSourceKind';
import type { IFormScope } from './IFormScope';
import type { IProtoForm } from './IProtoForm';
import { protoFormOf } from './protoForm';

/** Чи патч несе форму (будь-який варіант `forms`): глобальний — «Глобальна форма» прототипу (globalForms[модуль]). Чиста функція. */
export const hasForms = (patch: IRecordTypeOverride | undefined): boolean => !!patch?.forms && Object.keys(patch.forms).length > 0;

/**
 * Чи простір успадковує глобальну форму (прототип v4 lay(): m.layout || globalForms[ключ]): патч простору без власних форм і глобальний
 * патч із формою — сервер зливає глобальні форми, вкладки й ширини полів лише тоді (ADR-027). Чиста функція.
 */
export const inheritsGlobalForm = (patch: IRecordTypeOverride, inherited: IRecordTypeOverride | undefined): boolean =>
  !hasForms(patch) && hasForms(inherited);

/**
 * Модель редактора і звідки форма за рівнем (ADR-027: опубліковане → глобальне → простір; прототип v4 cust-form): глобальний рівень —
 * форма глобального патча над глобальним визначенням `rt` («global», щойно глобальний патч несе форму, інакше «std»); простір, що
 * успадковує глобальну форму, — вона сама (сервер уже злив її в `rt`, явні ширини — глобальні) з джерелом «global»; інакше — форма
 * простору чи стандартний макет (formSourceOf). Перевизначення простору з формою, яку записав не редактор (засіяні картки парламенту), —
 * теж «ws» (рішення 2 етапу 10: така форма діє замість глобальної, тож «Робочий простір», як у «Налаштування › Форми»). `base` — патч,
 * з якого прочитано модель (перша зміна в просторі пише всю успадковану форму в патч простору, як копія глобальної форми прототипу).
 * Чиста функція.
 */
export function scopeFormOf(rt: IRecordTypeDefinition, patch: IRecordTypeOverride, published: IRecordTypeDefinition | undefined, scope: IFormScope):
  { form: IProtoForm; source: FormSourceKind; base: IRecordTypeOverride } {
  if (scope.global) return { form: protoFormOf(rt, patch), source: hasForms(patch) ? 'global' : 'std', base: patch };
  if (inheritsGlobalForm(patch, scope.inherited)) return { form: protoFormOf(rt, scope.inherited!), source: 'global', base: scope.inherited! };
  const source = !editorWritten(patch) && hasForms(patch) ? 'ws' : formSourceOf(rt, patch, published);
  return { form: protoFormOf(rt, patch), source, base: patch };
}
